type RawEnv = Record<string, unknown>;

type EnvIssue = {
  key: string;
  message: string;
};

type RuntimeEnvContract = {
  mode: string;
  backendUrl: string;
  apiUrl: string;
  vapidPublicKey: string;
  analyticsDebug: boolean;
  sentryDsn: string;
  appVersion: string;
  recaptchaSiteKey: string;
  liveChatVendor: string;
  liveChatAppId: string;
  /** M5 (hard launch v2): real product analytics destination. */
  analyticsProvider: 'posthog' | 'ga4' | 'none';
  posthogKey: string;
  posthogHost: string;
  ga4MeasurementId: string;
  sentryTracesSampleRate: number;
  sentryReplaySessionSampleRate: number;
  stripePublicKey: string;
};

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');
const readEnv = (): RawEnv => ((import.meta as unknown as { env?: RawEnv }).env ?? {});

const readString = (env: RawEnv, key: string): string | undefined => {
  const raw = env[key];
  if (typeof raw !== 'string') return undefined;
  const normalized = raw.trim();
  return normalized.length > 0 ? normalized : undefined;
};

const readBoolean = (env: RawEnv, key: string, fallback = false): boolean => {
  const raw = readString(env, key);
  if (!raw) return fallback;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  throw new Error(`[env] ${key} must be \"true\" or \"false\".`);
};

const readNumber = (
  env: RawEnv,
  key: string,
  fallback: number,
  options?: { min?: number; max?: number }
): number => {
  const raw = readString(env, key);
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    throw new Error(`[env] ${key} must be a finite number. Received: \"${raw}\"`);
  }
  if (options?.min !== undefined && parsed < options.min) {
    throw new Error(`[env] ${key} must be between ${options.min} and ${options.max ?? '∞'}. Received: \"${raw}\"`);
  }
  if (options?.max !== undefined && parsed > options.max) {
    throw new Error(`[env] ${key} must be between ${options.min ?? '-∞'} and ${options.max}. Received: \"${raw}\"`);
  }
  return parsed;
};

const validateHttpUrl = (key: string, value: string): string => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('invalid protocol');
    }
    return trimTrailingSlash(url.toString());
  } catch {
    throw new Error(`[env] ${key} must be a valid http/https URL. Received: \"${value}\"`);
  }
};

const roleBasedHint = (key: string): string => {
  if (key === 'VITE_BACKEND_URL') {
    return 'Set the API origin used for auth/socket/API requests (e.g. https://api.ninposnacks.com).';
  }
  if (key === 'VITE_API_URL') {
    return 'Set push notification API origin, usually identical to VITE_BACKEND_URL.';
  }
  return 'Set this value in your runtime environment.';
};

const readRequiredUrl = (env: RawEnv, key: string, issues: EnvIssue[]): string => {
  const raw = readString(env, key);
  if (!raw) {
    issues.push({ key, message: `${key} is required. ${roleBasedHint(key)}` });
    return '';
  }

  try {
    return validateHttpUrl(key, raw);
  } catch (error) {
    issues.push({ key, message: error instanceof Error ? error.message : String(error) });
    return '';
  }
};

export type EnvValidationResult = {
  ok: boolean;
  issues: EnvIssue[];
};

const buildRuntimeEnv = (): { env: RuntimeEnvContract; validation: EnvValidationResult } => {
  const env = readEnv();
  const issues: EnvIssue[] = [];

  const mode = readString(env, 'MODE') ?? 'development';
  const backendUrl = readRequiredUrl(env, 'VITE_BACKEND_URL', issues);
  const apiUrl = readRequiredUrl(env, 'VITE_API_URL', issues);

  if (mode === 'production') {
    const recaptchaSiteKey = readString(env, 'VITE_RECAPTCHA_SITE_KEY') ?? '';
    if (!recaptchaSiteKey) {
      issues.push({
        key: 'VITE_RECAPTCHA_SITE_KEY',
        message: 'Set reCAPTCHA site key — required for web auth in production (must pair with server RECAPTCHA_SECRET_KEY).'
      });
    }
  }

  const runtimeEnv: RuntimeEnvContract = {
    mode,
    backendUrl,
    apiUrl,
    vapidPublicKey: readString(env, 'VITE_VAPID_PUBLIC_KEY') ?? '',
    analyticsDebug: readBoolean(env, 'VITE_ANALYTICS_DEBUG', false),
    sentryDsn: readString(env, 'VITE_SENTRY_DSN') ?? '',
    appVersion: readString(env, 'VITE_APP_VERSION') ?? '1.0.0',
    recaptchaSiteKey: readString(env, 'VITE_RECAPTCHA_SITE_KEY') ?? '',
    liveChatVendor: readString(env, 'VITE_LIVE_CHAT_VENDOR') ?? '',
    liveChatAppId: readString(env, 'VITE_LIVE_CHAT_APP_ID') ?? '',
    analyticsProvider: ((): 'posthog' | 'ga4' | 'none' => {
      const raw = (readString(env, 'VITE_ANALYTICS_PROVIDER') ?? '').toLowerCase();
      if (raw === 'posthog' || raw === 'ga4' || raw === 'none') return raw;
      return 'none';
    })(),
    posthogKey: readString(env, 'VITE_POSTHOG_KEY') ?? '',
    posthogHost: readString(env, 'VITE_POSTHOG_HOST') ?? 'https://us.i.posthog.com',
    ga4MeasurementId: readString(env, 'VITE_GA4_MEASUREMENT_ID') ?? '',
    sentryTracesSampleRate: readNumber(
      env,
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      mode === 'development' ? 1.0 : 0.1,
      { min: 0, max: 1 }
    ),
    sentryReplaySessionSampleRate: readNumber(
      env,
      'VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE',
      mode === 'development' ? 0.1 : 0,
      { min: 0, max: 1 }
    ),
    // Render web group historically used VITE_STRIPE_PUBLISHABLE_KEY; also accept VITE_STRIPE_PUBLIC_KEY.
    stripePublicKey:
      readString(env, 'VITE_STRIPE_PUBLISHABLE_KEY') ||
      readString(env, 'VITE_STRIPE_PUBLIC_KEY') ||
      ''
  };

  return {
    env: runtimeEnv,
    validation: {
      ok: issues.length === 0,
      issues
    }
  };
};

const runtime = buildRuntimeEnv();

export const validateRuntimeEnv = (): EnvValidationResult => runtime.validation;

export const assertRuntimeEnv = (): void => {
  if (runtime.validation.ok) return;
  const issueLines = runtime.validation.issues.map(issue => `- ${issue.key}: ${issue.message}`).join('\n');
  throw new Error(`[env] Invalid runtime configuration:\n${issueLines}\nRef: docs/dev/env.md`);
};

export const ENV = runtime.env;

export const BACKEND_URL = ENV.backendUrl;
export const API_URL = ENV.apiUrl;
export const VAPID_PUBLIC_KEY = ENV.vapidPublicKey;
export const IS_DEVELOPMENT = ENV.mode === 'development';
export const ANALYTICS_ENABLED = !IS_DEVELOPMENT || ENV.analyticsDebug;
export const SENTRY_DSN = ENV.sentryDsn;
export const APP_VERSION = ENV.appVersion;
export const RECAPTCHA_SITE_KEY = ENV.recaptchaSiteKey;

export const ANALYTICS_PROVIDER = ENV.analyticsProvider;
export const POSTHOG_KEY = ENV.posthogKey;
export const POSTHOG_HOST = ENV.posthogHost;
export const GA4_MEASUREMENT_ID = ENV.ga4MeasurementId;
export const SENTRY_TRACES_SAMPLE_RATE = ENV.sentryTracesSampleRate;
export const SENTRY_REPLAY_SESSION_SAMPLE_RATE = ENV.sentryReplaySessionSampleRate;
export const STRIPE_PUBLIC_KEY = ENV.stripePublicKey;
export const LIVE_CHAT_VENDOR = ENV.liveChatVendor;
export const LIVE_CHAT_APP_ID = ENV.liveChatAppId;
export const LIVE_CHAT_ENABLED = Boolean(LIVE_CHAT_VENDOR && LIVE_CHAT_APP_ID);
